import React from 'react';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';

type MenuItemSection = 'layout' | 'model' | 'removal';

/** Menu sections in display order, with their headings — as the expedition and reservation menus do. */
const SECTIONS: { id: MenuItemSection; title: (tr: (key: string, fallback: string) => string) => string }[] = [
  // Layout acts on the payload itself; Model on the entity; Removal takes work or the model back out.
  { id: 'layout', title: tr => tr('CalendarLayout.menu.layout', 'Layout') },
  { id: 'model', title: tr => tr('CalendarLayout.menu.model', 'Model') },
  { id: 'removal', title: tr => tr('CalendarLayout.menu.removal', 'Removal') },
];

interface ComponentProps {
  /** False for a model the caller may open but not change — a system-wide one seen by a customer. */
  editable: boolean;
  /** Admin-only actions (the system default) are hidden otherwise. */
  admin: boolean;
  published: boolean;
  archived: boolean;
  isDefault: boolean;
  onDuplicate: () => void;
  onRename: () => void;
  onSetDefault: () => void;
  onArchive: () => void;
  onDelete: () => void;
  /**
   * The layout payload as JSON. Only the model's own page offers it — from the list there is no
   * layout on screen for it to act on.
   */
  onExportImport?: () => void;
  /**
   * Undo-shaped actions, likewise only on the model's page. Each is meaningful in one state only,
   * so they are hidden rather than shown disabled.
   */
  draft?: { onDiscard: () => void; canDiscard: boolean; onRevert: () => void; canRevert: boolean };
  buttonMode?: 'outlined-rectangle' | 'table';
  dataTestId?: string;
}

/**
 * The secondary actions of one layout model, shared by the models list and the model's own page.
 *
 * Extracted so the two agree on what a model can do and how the actions are grouped: they were
 * drifting apart while each built its own section list. What differs between them is passed in —
 * the list has no layout on screen to export, and no draft to discard.
 */
export const CalendarLayoutModelMenu: React.FC<ComponentProps> = ({
  editable,
  admin,
  published,
  archived,
  isDefault,
  onDuplicate,
  onRename,
  onSetDefault,
  onArchive,
  onDelete,
  onExportImport,
  draft,
  buttonMode,
  dataTestId,
}) => {
  const { tr } = useTranslationLgs();

  interface MenuItemConfig {
    id: string;
    label: string;
    command: () => void;
    visible: boolean;
    section: MenuItemSection;
  }

  const model: MenuItemConfig[] = [
    {
      id: 'exportImport',
      label: editable ? tr('CalendarLayout.exportImport', 'Export / import') : tr('CalendarLayout.export', 'Export'),
      command: () => onExportImport?.(),
      visible: !!onExportImport,
      section: 'layout',
    },
    { id: 'duplicate', label: tr('CalendarLayout.duplicate', 'Duplicate'), command: onDuplicate, visible: true, section: 'model' },
    { id: 'rename', label: tr('CalendarLayout.rename', 'Rename'), command: onRename, visible: editable, section: 'model' },
    {
      id: 'setDefault',
      label: tr('CalendarLayout.setDefault', 'Set as default'),
      command: onSetDefault,
      visible: admin && published && !isDefault,
      section: 'model',
    },
    {
      id: 'discardDraft',
      label: tr('CalendarLayout.discardDraft', 'Discard draft'),
      command: () => draft?.onDiscard(),
      visible: editable && !!draft?.canDiscard,
      section: 'removal',
    },
    {
      id: 'revert',
      label: tr('CalendarLayout.revert', 'Revert publish'),
      command: () => draft?.onRevert(),
      visible: editable && !!draft?.canRevert,
      section: 'removal',
    },
    { id: 'archive', label: tr('CalendarLayout.archive', 'Archive'), command: onArchive, visible: editable && !archived, section: 'removal' },
    // Always offered, not only for an unused model: hiding it left no way to tell "you may not
    // delete this" from "this button does not exist".
    { id: 'delete', label: tr('CalendarLayout.delete', 'Delete'), command: onDelete, visible: editable, section: 'removal' },
  ];

  return (
    <MoreButton
      buttonMode={buttonMode}
      dataTestId={dataTestId}
      sections={SECTIONS.map(section => ({
        title: section.title(tr),
        items: model.filter(item => item.section === section.id),
      }))}
    />
  );
};
